import React, { useEffect, useRef, useState } from 'react';
import './App.css';

// ─── Scroll reveal hook ───────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const links = ['Services', 'Work', 'Process', 'Pricing', 'About', 'Contact'];
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/5' : ''}`}>
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <a href="#hero">
          <img src="/logo.png" alt="Zuhz
          eye" className="h-20








          w-auto" />
        </a>
        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`}
              className="text-sm text-white/60 hover:text-white transition-colors duration-200 tracking-wide">
              {l}
            </a>
          ))}
        </div>
        <a href="#contact"
          className="hidden md:inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-glow text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-[0_0_24px_rgba(139,47,230,0.5)]">
          Get a Quote
        </a>
        {/* Mobile hamburger */}
        <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)}>
          <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}/>
          <div className={`w-6 h-0.5 bg-white mb-1.5 transition-all ${menuOpen ? 'opacity-0' : ''}`}/>
          <div className={`w-6 h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}/>
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5 px-6 pb-6">
          {links.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`}
              onClick={() => setMenuOpen(false)}
              className="block py-3 text-white/70 hover:text-white border-b border-white/5 text-sm tracking-wide">
              {l}
            </a>
          ))}
          <a href="#contact" onClick={() => setMenuOpen(false)}
            className="mt-4 block text-center bg-brand-primary text-white py-3 rounded-full text-sm font-semibold">
            Get a Quote
          </a>
        </div>
      )}
    </nav>
  );
}

// ─── HERO ────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-dark-charcoal">
      <div className="grain-overlay" />
      {/* Glow blob */}
      <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse-glow" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-6">
            Emmy-Nominated Video Production
          </p>
          <h1 className="reveal font-display font-bold leading-[0.9] mb-8" style={{animationDelay:'0.1s'}}>
            <span className="block text-[clamp(3.5rem,9vw,8rem)] text-white">Your Story,</span>
            <span className="block text-[clamp(3.5rem,9vw,8rem)] text-stroke">Told</span>
            <span className="block text-[clamp(3.5rem,9vw,8rem)] text-white">Right.</span>
          </h1>
          <p className="reveal text-white/55 text-lg md:text-xl max-w-xl leading-relaxed mb-10" style={{animationDelay:'0.2s'}}>
            Corporate films, documentaries, events, podcasts, and brand stories — shaped by twenty-five years behind the camera and in the edit suite.
          </p>
          <div className="reveal flex flex-wrap gap-4" style={{animationDelay:'0.3s'}}>
            <a href="#contact"
              className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-glow text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_32px_rgba(139,47,230,0.6)] text-base">
              Start a Project →
            </a>
            <a href="#work"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 text-base">
              View Showreel
            </a>
          </div>
        </div>
        {/* ─ MASCOT: drop your mascot PNG into public/mascot.png and it shows here ─ */}
        <img
          src="/mascot.png"
          alt="Zuhzeye mascot"
          data-testid="hero-mascot"
          onError={e => { e.target.style.display = 'none'; }}
          className="hidden md:block absolute z-20 right-[-2%] lg:right-[2%] bottom-[-8%] w-[34vw] max-w-[520px] min-w-[300px] pointer-events-none drop-shadow-[0_0_60px_rgba(139,47,230,0.55)] animate-float-slow mascot-blend-lighten"
        />
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-white text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white to-transparent" />
      </div>
    </section>
  );
}

// ─── MARQUEE ─────────────────────────────────────────────────────────────────
function Marquee() {
  const items = ['Corporate Video', 'Training Films', 'Documentary', 'Event Coverage', 'Photography', 'Podcast Production', 'PSAs & Brand Films', 'AI-Assisted B-Roll'];
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-white/8 bg-deep-purple/40 py-4">
      <div className="marquee-track animate-marquee">
        {doubled.map((item, i) => (
          <span key={i} className="mx-8 text-white/50 text-sm tracking-[0.2em] uppercase font-medium">
            {item} <span className="text-brand-primary mx-4">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── SERVICES ────────────────────────────────────────────────────────────────
const services = [
  { icon: '🎬', title: 'AI Video & Motion', desc: 'Hyper-realistic AI-generated video, cinematic motion graphics, and seamless VFX compositing that feels impossibly premium.', featured: true },
  { icon: '✦', title: 'Brand Identity', desc: 'Logo systems, visual language, and brand strategy that lives across every medium.' },
  { icon: '📱', title: 'Social Content', desc: 'Scroll-stopping content engineered for virality and sustained engagement.' },
  { icon: '🌐', title: 'Web Experiences', desc: 'Immersive websites and landing pages that convert visitors into believers.' },
  { icon: '🎵', title: 'Audio Branding', desc: 'Sonic identities, sound design, and music supervision that make your brand audible.' },
  { icon: '🚀', title: 'Campaign Strategy', desc: 'Full-funnel creative strategy from concept to conversion, backed by data.' },
];

function Services() {
  return (
    <section id="services" className="py-32 bg-dark-charcoal">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">What I Do</p>
          <h2 className="reveal font-display font-bold text-[clamp(2.5rem,5vw,4rem)] text-white leading-tight">
            Services Built for<br/><span className="text-brand-primary">Impact</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => (
            <div key={i}
              className={`reveal group relative p-8 rounded-2xl border transition-all duration-500 cursor-default
                ${s.featured
                  ? 'glow-ring bg-gradient-to-br from-brand-dark/80 to-deep-purple/60 border-brand-primary/40'
                  : 'bg-white/3 border-white/8 hover:border-brand-primary/30 hover:bg-white/5'}`}
              style={{ animationDelay: `${i * 0.07}s` }}>
              {s.featured && <span className="absolute top-4 right-4 text-xs text-brand-glow bg-brand-primary/20 px-2 py-1 rounded-full">Flagship</span>}
              <div className="text-3xl mb-4">{s.icon}</div>
              <h3 className="font-display font-bold text-xl text-white mb-3">{s.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SHOWREEL ────────────────────────────────────────────────────────────────
function Showreel() {
  return (
    <section id="work" className="py-32 bg-deep-purple/20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">My Work</p>
          <h2 className="reveal font-display font-bold text-[clamp(2.5rem,5vw,4rem)] text-white">See It in Motion</h2>
        </div>
        {/*
          ── SHOWREEL VIDEO ──────────────────────────────────────────────────────
          To add your video, replace the placeholder below with your embed URL:

          YouTube example:
            src="https://www.youtube.com/embed/YOUR_VIDEO_ID?autoplay=0&rel=0"

          Vimeo example:
            src="https://player.vimeo.com/video/YOUR_VIDEO_ID?color=8B2FE6"

          Just swap out the src on the iframe below.
          ───────────────────────────────────────────────────────────────────── */}
        <div className="reveal relative rounded-3xl overflow-hidden glow-ring aspect-video">
          {/* ↓↓↓ Replace this src with your YouTube or Vimeo embed URL ↓↓↓ */}
          <iframe
            src="https://www.youtube.com/embed/OZfprnWSHo8?rel=0"
            title="Zuhzeye Showreel"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
          {/* Fallback overlay shown when no video is set */}
          <div className="hidden">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-brand-primary/30 border border-brand-primary flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white translate-x-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <p className="text-white/70 text-sm tracking-widest uppercase">Your Showreel</p>
              <p className="text-white/30 text-xs mt-1">Add your YouTube or Vimeo link in App.js</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── PROCESS ─────────────────────────────────────────────────────────────────
const steps = [
  { num: '01', title: 'Discovery', desc: 'Your goal, your audience, and the story you need to tell, pinned down before a camera comes out — so the whole project aims at one clear finish line.' },
  { num: '02', title: 'Pre-Production', desc: 'Script, shot list, locations, and schedule, all planned and approved before shoot day. No surprises once production starts.' },
  { num: '03', title: 'Production', desc: 'The shoot itself — directed and captured with the efficiency of twenty-five years on set.' },
  { num: '04', title: 'Post & Delivery', desc: 'Editing, color, sound, captions, and graphics, finished to broadcast standard and delivered in every format you need.' },
];

function Process() {
  return (
    <section id="process" className="py-32 bg-dark-charcoal">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">How I Work</p>
          <h2 className="reveal font-display font-bold text-[clamp(2.5rem,5vw,4rem)] text-white">The Process</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="reveal relative" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="font-display font-bold text-[5rem] leading-none text-white/5 mb-4 select-none">{s.num}</div>
              <div className="w-8 h-0.5 bg-brand-primary mb-4" />
              <h3 className="font-display font-bold text-xl text-white mb-3">{s.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PRICING ─────────────────────────────────────────────────────────────────
const plans = [
  {
    name: 'Single Project',
    price: 'Custom',
    period: '',
    desc: 'One video done right — a training piece, brand film, event, or documentary short.',
    features: ['Discovery and scripting', 'Full production and direction', 'Editing, color, sound, and graphics', 'Captions and every delivery format', 'Two revision rounds'],
  },
  {
    name: 'Monthly Retainer',
    price: 'Custom',
    period: '',
    desc: 'A set number of videos every month — built for organizations with an ongoing story to tell.',
    features: ['Videos on a monthly cadence', 'Consistent look and voice', 'Priority scheduling', 'Ongoing creative direction', 'Predictable monthly cost'],
    featured: true,
  },
  {
    name: 'Full Production',
    price: 'Custom',
    period: '',
    desc: 'Larger documentary or campaign work with multiple shoot days and deliverables.',
    features: ['Multi-day shoots', 'Full crew coordination', 'Multiple cuts and edits', 'Broadcast-standard finishing', 'On-site direction'],
  },
];

function Pricing() {
  return (
    <section id="pricing" className="py-32 bg-deep-purple/20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">Investment</p>
          <h2 className="reveal font-display font-bold text-[clamp(2.5rem,5vw,4rem)] text-white">Simple Pricing</h2>
          <p className="reveal text-white/50 mt-4 max-w-md mx-auto">No hidden fees. No surprises. Just premium work at a fair price.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <div key={i}
              className={`reveal relative p-8 rounded-2xl border transition-all duration-300
                ${p.featured
                  ? 'glow-ring bg-gradient-to-br from-brand-dark/80 to-deep-purple/60 border-brand-primary/50 scale-105'
                  : 'bg-white/3 border-white/8 hover:border-white/20'}`}
              style={{ animationDelay: `${i * 0.1}s` }}>
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs bg-brand-primary text-white px-4 py-1 rounded-full font-semibold tracking-wide">
                  Most Popular
                </span>
              )}
              <h3 className="font-display font-bold text-xl text-white mb-1">{p.name}</h3>
              <div className="flex items-end gap-1 my-4">
                <span className="font-display font-bold text-4xl text-white">{p.price}</span>
                <span className="text-white/40 text-sm pb-1">{p.period}</span>
              </div>
              <p className="text-white/50 text-sm mb-6 leading-relaxed">{p.desc}</p>
              <ul className="space-y-2.5 mb-8">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                    <span className="text-brand-glow text-xs">✦</span> {f}
                  </li>
                ))}
              </ul>
              <a href="#contact"
                className={`block text-center font-semibold py-3 rounded-full transition-all duration-300 text-sm
                  ${p.featured
                    ? 'bg-brand-primary hover:bg-brand-glow text-white hover:shadow-[0_0_24px_rgba(139,47,230,0.5)]'
                    : 'border border-white/20 text-white hover:border-brand-primary/50 hover:bg-brand-primary/10'}`}>
                Get Started
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ABOUT ───────────────────────────────────────────────────────────────────
function About() {
  return (
    <section id="about" className="py-32 bg-dark-charcoal overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Mascot / visual */}
          <div className="reveal relative flex items-center justify-center order-2 lg:order-1">
            <div className="absolute inset-0 bg-radial-purple" />
            <img
              src="/mascot.png"
              alt="Zuhzeye mascot"
              data-testid="about-mascot"
              onError={e => { e.target.style.display = 'none'; }}
              className="relative w-[80%] max-w-[480px] drop-shadow-[0_0_60px_rgba(199,125,255,0.5)] animate-float-slow mascot-blend-lighten"
            />
          </div>
          {/* Text */}
          <div className="order-1 lg:order-2">
            <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">About Me</p>
            <h2 className="reveal font-display font-bold text-[clamp(2.5rem,4vw,3.5rem)] text-white leading-tight mb-6">
              I'm Mark Groce.<br />25 Years Behind the Camera.
            </h2>
            <p className="reveal text-white/55 leading-relaxed mb-4">
              For twenty-five years I produced and directed video inside New York City Transit — training programs, public-service campaigns, and documentaries seen across one of the largest transit systems in the world. That work earned an Emmy nomination, three Telly Awards, and a NATOA Government Programming Award.
            </p>
            <p className="reveal text-white/55 leading-relaxed mb-8">
              Zuhzeye Media brings that same broadcast-grade craft to your organization. You work directly with me, start to finish — no account managers, no handoffs, no junior team learning on your budget. Just your story, told right.
            </p>
            <div className="reveal grid grid-cols-3 gap-6">
              {[['200+','Projects Delivered'],['98%','Client Retention'],['4.9★','Average Rating']].map(([val, label]) => (
                <div key={label}>
                  <div className="font-display font-bold text-3xl text-brand-glow">{val}</div>
                  <div className="text-white/40 text-xs mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── CTA BANNER ───────────────────────────────────────────────────────────────
function CTABanner() {
  return (
    <section className="py-24 bg-gradient-to-r from-brand-dark via-brand-primary/50 to-deep-purple relative overflow-hidden">
      <div className="grain-overlay" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="reveal font-display font-bold text-[clamp(2rem,5vw,3.5rem)] text-white leading-tight mb-6">
          Ready to Tell Your Story?
        </h2>
        <p className="reveal text-white/70 text-lg mb-10 max-w-xl mx-auto">
          Tell me about your project and I'll get back to you within a day with a plan and a quote.
        </p>
        <a href="#contact"
          className="reveal inline-flex items-center gap-2 bg-white text-brand-primary hover:bg-brand-glow hover:text-white font-bold px-10 py-4 rounded-full transition-all duration-300 text-base hover:shadow-[0_0_40px_rgba(199,125,255,0.5)]">
          Let's Build Together →
        </a>
      </div>
    </section>
  );
}

// ─── CONTACT ─────────────────────────────────────────────────────────────────
function Contact() {
  const [form, setForm] = useState({ name:'', email:'', company:'', service:'', budget:'', message:'' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      if (backendUrl) {
        const res = await fetch(`${backendUrl}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error('Server error');
      }
      setStatus('success');
      setForm({ name:'', email:'', company:'', service:'', budget:'', message:'' });
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 focus:border-brand-primary text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm outline-none transition-colors duration-200";
  const services = ['Corporate & Training Video', 'Documentary & Feature Stories', 'Event Videography & Photography', 'Podcast Production', 'PSAs & Brand Films', 'AI-Assisted Video & B-Roll', 'Other'];
  const budgets = ['Under $2,500', '$2,500 – $5k', '$5k – $15k', '$15k – $50k', '$50k+'];

  return (
    <section id="contact" className="py-32 bg-dark-charcoal">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="reveal text-brand-glow text-sm font-semibold tracking-[0.25em] uppercase mb-4">Get in Touch</p>
          <h2 className="reveal font-display font-bold text-[clamp(2.5rem,5vw,4rem)] text-white">Start Your Project</h2>
          <p className="reveal text-white/60 mt-6 text-base">
            Prefer email? Reach me directly at <a href="mailto:zuhzeyemedia@gmail.com" className="text-brand-glow hover:text-white underline transition-colors">zuhzeyemedia@gmail.com</a>
          </p>
        </div>
        {status === 'success' ? (
          <div className="reveal text-center py-20">
            <div className="text-5xl mb-4">✦</div>
            <h3 className="font-display font-bold text-2xl text-white mb-2">Message Received!</h3>
            <p className="text-white/55">I'll be in touch within a day.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="reveal space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input name="name" value={form.name} onChange={handleChange} required placeholder="Your Name *" className={inputClass} />
              <input name="email" value={form.email} onChange={handleChange} required type="email" placeholder="Email Address *" className={inputClass} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input name="company" value={form.company} onChange={handleChange} placeholder="Company (optional)" className={inputClass} />
              <select name="service" value={form.service} onChange={handleChange} className={inputClass}>
                <option value="" className="bg-gray-900">Select a Service</option>
                {services.map(s => <option key={s} value={s} className="bg-gray-900">{s}</option>)}
              </select>
            </div>
            <select name="budget" value={form.budget} onChange={handleChange} className={inputClass}>
              <option value="" className="bg-gray-900">Budget Range</option>
              {budgets.map(b => <option key={b} value={b} className="bg-gray-900">{b}</option>)}
            </select>
            <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
              placeholder="Tell us about your project *" className={inputClass + ' resize-none'} />
            {status === 'error' && <p className="text-red-400 text-sm">Something went wrong. Please try again or email us directly.</p>}
            <button type="submit" disabled={status === 'sending'}
              className="w-full bg-brand-primary hover:bg-brand-glow text-white font-bold py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_32px_rgba(139,47,230,0.5)] disabled:opacity-50 disabled:cursor-not-allowed">
              {status === 'sending' ? 'Sending...' : 'Send Message →'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-black border-t border-white/5 pt-16 pb-8 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-2">
            <div className="font-display font-bold text-2xl tracking-widest text-white mb-4">ZUHZEYE</div>
            <p className="text-white/40 text-sm leading-relaxed max-w-xs">
              Emmy-nominated video production — corporate films, documentaries, events, and brand stories, shaped by twenty-five years behind the camera.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Services</h4>
            {['Corporate & Training Video','Documentary & Feature Stories','Event Videography & Photography','Podcast Production','PSAs & Brand Films','AI-Assisted Video & B-Roll'].map(s => (
              <p key={s} className="text-white/40 text-sm mb-2 hover:text-white/70 cursor-pointer transition-colors">{s}</p>
            ))}
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Connect</h4>
            {['YouTube','LinkedIn'].map(s => (
              <p key={s} className="text-white/40 text-sm mb-2 hover:text-white/70 cursor-pointer transition-colors">{s}</p>
            ))}
          </div>
        </div>
        {/* Giant wordmark */}
        <div className="relative overflow-hidden py-8 mb-8 border-t border-white/5">
          <p className="font-display font-bold text-[clamp(4rem,14vw,10rem)] text-white/4 leading-none select-none tracking-widest text-center">
            ZUHZEYE
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white/25 text-xs">
          <p>© {new Date().getFullYear()} Zuhzeye Media. All rights reserved.</p>
          <p>Your Story, Told Right.</p>
        </div>
      </div>
    </footer>
  );
}

// ─── APP ROOT ────────────────────────────────────────────────────────────────
export default function App() {
  useReveal();
  return (
    <div className="App">
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <Showreel />
      <Process />
      <Pricing />
      <About />
      <CTABanner />
      <Contact />
      <Footer />
    </div>
  );
}
